/**
 * Perfiles de acceso de la Agenda Quirúrgica MAC
 * (según "Autorización de perfiles y accesos por puesto").
 *
 *  - agenda     → gestiona y programa: reserva, confirma, reagenda, cancela,
 *                 registra tiempos / realizado. Solo su unidad.
 *  - consulta   → solo lectura de su unidad (puede descargar Excel/PDF).
 *  - mesa_ayuda → solo lectura de TODAS las unidades con un solo usuario
 *                 + gestión de usuarios (alta, activar / inactivar).
 *  - monitor    → pantallas / estaciones de trabajo: solo la ventana Agenda
 *                 de su unidad, en solo lectura, sin descargas; la fecha se
 *                 mantiene en el día actual y la vista se actualiza sola.
 *
 * Los niveles anteriores se siguen reconociendo para no romper usuarios
 * ya registrados:
 *   administrador → consulta (en "Corporativo" con vista global: cambia de
 *                   unidad y ve "Todas las unidades")
 *   tablero / lectura → consulta
 *   soporte / mesa → mesa_ayuda
 *   usuario       → agenda
 */

export const PERFILES = [
    { value: 'agenda',     label: 'Agenda' },
    { value: 'consulta',   label: 'Consulta (solo lectura)' },
    { value: 'mesa_ayuda', label: 'Mesa de Ayuda' },
    { value: 'monitor',    label: 'Monitor (pantalla de Agenda)' },
]

const ETIQUETAS = {
    agenda:     'Agenda',
    consulta:   'Consulta',
    mesa_ayuda: 'Mesa de Ayuda',
    monitor:    'Monitor',
}

const EQUIVALENCIAS = {
    agenda:        'agenda',
    usuario:       'agenda',
    consulta:      'consulta',
    tablero:       'consulta',
    lectura:       'consulta',
    administrador: 'consulta',
    mesa_ayuda:    'mesa_ayuda',
    'mesa de ayuda': 'mesa_ayuda',
    mesa:          'mesa_ayuda',
    soporte:       'mesa_ayuda',
    monitor:       'monitor',
    pantalla:      'monitor',
}

/** Minúsculas, sin acentos, y guiones / guiones bajos / espacios repetidos como un solo espacio. */
const limpiar = (nivel) =>
    (nivel || '').toString()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[\s_-]+/g, ' ').trim()

// Equivalencias con la clave ya limpia (mesa_ayuda → "mesa ayuda")
const EQUIVALENCIAS_LIMPIAS = Object.fromEntries(
    Object.entries(EQUIVALENCIAS).map(([k, v]) => [limpiar(k), v])
)

/**
 * Palabras clave para niveles escritos con variantes
 * (ej. "Soporte TI", "Soporte Técnico", "Administrador Unidad").
 * Se revisan en este orden; el primero que coincide gana.
 */
const PALABRAS_CLAVE = [
    [/monitor|pantalla/,                            'monitor'],
    [/soporte|support|\bmesa\b|help ?desk/,       'mesa_ayuda'],
    [/admin/,                                       'consulta'],
    [/lectura|tablero|consulta/,                    'consulta'],
    [/agenda|usuario/,                              'agenda'],
]

/** Convierte cualquier nivel (nuevo o anterior) a uno de los perfiles. */
export const normalizarPerfil = (nivel) => {
    const clave = limpiar(nivel)
    if (EQUIVALENCIAS_LIMPIAS[clave]) return EQUIVALENCIAS_LIMPIAS[clave]
    const regla = PALABRAS_CLAVE.find(([re]) => re.test(clave))
    return regla ? regla[1] : 'consulta'
}

/**
 * Obtiene el nivel registrado de un usuario de Firestore.
 * Primero busca un nivel propio de la Agenda (Systems[id=2]) y después el
 * nivel general; acepta variantes de nombre de campo usadas en usuarios
 * antiguos (Permiso, permiso, Nivel, nivel, level, rol, Rol, Perfil, perfil).
 * @param {Object} data Documento del usuario
 * @returns {String} el nivel tal cual está guardado ('' si no hay)
 */
const CAMPOS_NIVEL = ['Permiso', 'permiso', 'Nivel', 'nivel', 'level', 'Level', 'Rol', 'rol', 'Perfil', 'perfil']
export const obtenerNivelUsuario = (data = {}) => {
    const sistema = (data.Systems || []).find((s) => s && (s.id === 2 || s.id === '2')) || {}
    for (const origen of [sistema, data]) {
        for (const campo of CAMPOS_NIVEL) {
            const v = origen[campo]
            if (typeof v === 'string' && v.trim()) return v.trim()
        }
    }
    return ''
}

/** Perfil efectivo (se conserva la firma por compatibilidad). */
// eslint-disable-next-line no-unused-vars
export const perfilEfectivo = (nivel, isCorporativo = false) => normalizarPerfil(nivel)

/** Etiqueta legible del perfil. */
export const etiquetaPerfil = (nivel, isCorporativo = false) => ETIQUETAS[perfilEfectivo(nivel, isCorporativo)]

/**
 * Permisos efectivos del usuario.
 * @param {String}  nivel         Campo Permiso del usuario
 * @param {Boolean} isCorporativo true si su unidad es "Corporativo"
 */
export const obtenerPermisos = (nivel, isCorporativo = false) => {
    const perfil = perfilEfectivo(nivel, isCorporativo)

    // Monitor: solo la ventana Agenda de su unidad, sin acciones ni descargas.
    if (perfil === 'monitor') {
        return {
            perfil,
            etiqueta:        ETIQUETAS[perfil],
            vistaGlobal:     false,
            soloLectura:     true,
            gestionUsuarios: false,
            puedeDescargar:  false,
            soloAgenda:      true,
        }
    }

    // "Corporativo" no tiene quirófanos, así que Consulta asignada ahí
    // (incluido el antiguo administrador) ve todas las unidades.
    const vistaGlobal =
        perfil === 'mesa_ayuda' ||
        (isCorporativo && perfil === 'consulta')

    return {
        perfil,
        etiqueta:        ETIQUETAS[perfil],
        vistaGlobal,
        soloLectura:     vistaGlobal || perfil === 'consulta',
        gestionUsuarios: perfil === 'mesa_ayuda',
        puedeDescargar:  true,
        soloAgenda:      false,
    }
}
